import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ReviewStatus } from '../../enum/review.status';
import { SubmissionStatus } from '../../enum/submission.status';
import { InfoService } from '../../service/info.service';
import { PreferencesService } from '../../service/preferences.service';

// Card "novo" de revisão — extraído de Minhas revisões para reutilização em
// busca global (autor) e busca dentro do evento (admin). Estrutura "variant D"
// preservada do original. Coluna de ações (`reviewConfig`) e coluna de
// arquivos (`showFiles`) controladas via inputs para adaptar ao contexto.
@Component({
  selector: 'app-review-card-new',
  templateUrl: './review-card-new.component.html',
  styleUrls: ['./review-card-new.component.scss'],
})
export class ReviewCardNewComponent {
  @Input() review: any;
  // Configuração global do bloco de revisão (necessária pra <app-paper-review-actions>).
  // Quando null/undefined a coluna de ações some — caso de busca.
  @Input() reviewConfig: any = null;
  // Mostra a coluna direita de arquivos. Default true. Buscas costumam passar false.
  @Input() showFiles = true;
  // Checkbox de seleção aparece por DEFAULT. Consumidor sem seleção passa
  // [selectable]="false" pra escondê-lo (vide event-user-view).
  @Input() selectable = true;
  @Input() selected = false;
  @Output() selectionChange = new EventEmitter<boolean>();

  constructor(
    public preferencesService: PreferencesService,
    private infoService: InfoService,
  ) {}

  public getStatusIcon(status: string) {
    return ReviewStatus.icon(status as ReviewStatus);
  }

  /** Ícone do status da SUBMISSÃO — usado no círculo abaixo do ID pra
   *  manter consistência de vocabulário com o card de "Minhas submissões". */
  public getSubmissionStatusIcon(status: string) {
    return SubmissionStatus.icon(status as SubmissionStatus);
  }

  public isMeTheReviewer(review: any): boolean {
    return review?.user?.id === this.infoService.user?.id;
  }

  /**
   * Estado de "posse" da revisão da perspectiva do user logado:
   *  - `'delegated'`: EU deleguei pra outro (eu = assignedBy, outro = user)
   *  - `'received'`:  outro delegou pra MIM (eu = user, outro = assignedBy)
   *  - `null`: revisão própria sem delegação (eu = user E é primeira assign)
   */
  public getOwnershipState(review: any): 'delegated' | 'received' | null {
    if (!review?.user) { return null; }
    if (!this.isMeTheReviewer(review)) {
      // Eu vejo este card → eu deleguei (caso de Minhas revisões / event-
      // user-view onde só aparecem revisões em que sou user OU assignedBy).
      return 'delegated';
    }
    // Sou o reviewer atual. Se NÃO é primeira atribuição, recebi via delegação.
    if (review?.isFirstAssign === false && review?.assignedBy) {
      return 'received';
    }
    return null;
  }

  /**
   * Variante ANÔNIMA da ribbon: só para quem realmente delegou.
   *
   * `getOwnershipState` infere "eu deleguei" apenas de `!isMeTheReviewer`,
   * apoiado na premissa de que naqueles contextos só aparecem revisões em que
   * sou `user` ou `assignedBy`. A premissa NÃO vale no `event-user-view`, onde
   * o chair vê as revisões de outra pessoa — e ali o tooltip ("você delegou")
   * seria dirigido à pessoa errada. Por isso aqui a checagem é explícita
   * contra `assignedBy`, em vez de reaproveitar a inferência.
   */
  public isAnonymousDelegationByMe(review: any): boolean {
    if (!review?.anonymity) { return false; }
    const assignedById = review?.assignedBy?.id ?? review?.assignedBy;
    return assignedById === this.infoService.user?.id;
  }

  /** Nome+afiliação do "outro lado" da delegação pro chip ownership.
   *  Quando deleguei: o outro é `review.user` (delegate).
   *  Quando recebi:   o outro é `review.assignedBy` (delegator original). */
  public getOwnershipOther(review: any): { name: string, aff?: string } | null {
    const state = this.getOwnershipState(review);
    if (!state) { return null; }
    const other = state === 'delegated' ? review?.user : review?.assignedBy;
    if (!other?.name) { return null; }
    return {
      name: other.name,
      aff: other?.profile?.affiliation?.acronym,
    };
  }

  /**
   * Decide qual frase narrativa exibir na linha de revisão do card.
   * Precedência:
   *   1. `'delegated'` — eu deleguei essa revisão (vence sobre o status
   *      cru, pois a história "delegação" é mais relevante que o status
   *      do delegate naquele momento).
   *   2. status com frase específica (NOTIFIED, CONFIRMED, ...) — chip
   *      embutido no meio do texto.
   *   3. `null` — sem frase, cai no fallback chip+ownership.
   */
  public getReviewPhraseKey(review: any): string | null {
    if (this.getOwnershipState(review) === 'delegated') { return 'DELEGATED_BY_ME'; }
    const status = (review?.status || '').toString().toUpperCase();
    if (['NOTIFIED', 'CONFIRMED'].includes(status)) { return status; }
    return null;
  }

  /** Atalho usado nos `*ngIf` do template — sem frase = cai no fallback. */
  public hasReviewPhrase(review: any): boolean {
    return this.getReviewPhraseKey(review) !== null;
  }

  public getTrackFiles(submission: any): any[] {
    return (submission?.track?.trackFiles || []);
  }

  public findUploadedFile(submission: any, trackFile: any): any | null {
    if (!submission?.files || !trackFile) { return null; }
    // O payload de revisão traz `file.trackFile` SEM `id` (só name/required/closeAt),
    // então casar por id falha e o arquivo enviado renderiza como "ausente". Casa
    // por id quando disponível e por nome como fallback (o trackFile do card vem
    // enxertado de `event.tracks`, com id + name).
    return submission.files.find((f: any) => {
      const ftf = f?.trackFile;
      if (!ftf) { return false; }
      return (ftf.id != null && ftf.id === trackFile.id)
          || (ftf.name != null && ftf.name === trackFile.name);
    }) || null;
  }
}
